import L from "leaflet";

export interface MarkerIconConfig {
  selectedGradient: string;
  unselectedGradient: string;
  selectedArrowColor: string;
  unselectedArrowColor: string;
  width: number;
  height: number;
  text: string;
  fontSize: string;
  className: string;
  iconSize: [number, number];
  iconAnchor: [number, number];
}

const createMarkerIcon = (
  isSelected: boolean,
  isInteractionDisabled: boolean,
  config: MarkerIconConfig,
): L.DivIcon => {
  const {
    selectedGradient,
    unselectedGradient,
    selectedArrowColor,
    unselectedArrowColor,
    width,
    height,
    text,
    fontSize,
    className,
    iconSize,
    iconAnchor,
  } = config;

  const background = isSelected ? selectedGradient : unselectedGradient;
  const arrowColor = isSelected ? selectedArrowColor : unselectedArrowColor;

  return L.divIcon({
    html: `
      <div style="
        background: ${background};
        color: white;
        border-radius: ${height === 36 && width === 36 ? "50%" : "8px"};
        width: ${width}px;
        height: ${height}px;
        display: flex;
        align-items: center;
        justify-content: center;
        font-weight: bold;
        font-size: ${fontSize};
        border: 3px solid white;
        box-shadow: 0 4px 12px rgba(0,0,0,0.4);
        position: relative;
        cursor: ${isInteractionDisabled ? "default" : "pointer"};
        transition: all 0.2s ease;
      "
      ${
        !isInteractionDisabled
          ? `
        onmouseover="this.style.transform='scale(1.1)'"
        onmouseout="this.style.transform='scale(1)'"
      `
          : ""
      }
      >
        ${text}
        ${
          height === 36 && width === 80
            ? `
        <div style="
          position: absolute;
          bottom: -8px;
          left: 50%;
          transform: translateX(-50%);
          width: 0;
          height: 0;
          border-left: 8px solid transparent;
          border-right: 8px solid transparent;
          border-top: 8px solid ${arrowColor};
        "></div>
      `
            : ""
        }
      </div>
    `,
    className,
    iconSize,
    iconAnchor,
  });
};

export const markerConfigs = {
  referencePoint: {
    selectedGradient: "linear-gradient(135deg, #059669, #10B981)",
    unselectedGradient: "linear-gradient(135deg, #10B981, #34D399)",
    selectedArrowColor: "#10B981",
    unselectedArrowColor: "#34D399",
    width: 80,
    height: 36,
    text: "INÍCIO",
    fontSize: "10px",
    className: "headquarters-marker",
    iconSize: [80, 44] as [number, number],
    iconAnchor: [40, 44] as [number, number],
  },
  customer: {
    selectedGradient: "linear-gradient(135deg, #DC2626, #EF4444)",
    unselectedGradient: "linear-gradient(135deg, #6B7280, #9CA3AF)",
    selectedArrowColor: "#EF4444",
    unselectedArrowColor: "#9CA3AF",
    width: 36,
    height: 36,
    text: " ",
    fontSize: "14px",
    className: "custom-marker",
    iconSize: [36, 36] as [number, number],
    iconAnchor: [18, 18] as [number, number],
  },
};

export const createReferencePointIcon = (
  isSelected: boolean,
  isInteractionDisabled: boolean,
): L.DivIcon => {
  return createMarkerIcon(
    isSelected,
    isInteractionDisabled,
    markerConfigs.referencePoint,
  );
};

export const createCustomerIcon = (
  isSelected: boolean,
  isInteractionDisabled: boolean,
  text?: string,
): L.DivIcon => {
  const config = { ...markerConfigs.customer };
  if (text) {
    config.text = text;
    config.fontSize = "12px";
  }
  return createMarkerIcon(isSelected, isInteractionDisabled, config);
};

export const createCustomMarkerIcon = (
  isSelected: boolean,
  isInteractionDisabled: boolean,
  config: Partial<MarkerIconConfig> & { text: string },
): L.DivIcon => {
  const defaultConfig: MarkerIconConfig = {
    selectedGradient: "linear-gradient(135deg, #059669, #10B981)",
    unselectedGradient: "linear-gradient(135deg, #10B981, #34D399)",
    selectedArrowColor: "#10B981",
    unselectedArrowColor: "#34D399",
    width: 36,
    height: 36,
    text: "",
    fontSize: "14px",
    className: "custom-marker",
    iconSize: [36, 36],
    iconAnchor: [18, 18],
  };

  const finalConfig = { ...defaultConfig, ...config };
  return createMarkerIcon(isSelected, isInteractionDisabled, finalConfig);
};

export default createMarkerIcon;
